import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Eye, LogIn, LogOut, Send, ShieldCheck, History, Lock, MessageSquare, Loader2, AlertTriangle,
} from "lucide-react";
import { timeAgo } from "@/lib/constants";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function MessageRow({ m }) {
  if (m.system) {
    return (
      <div className="flex justify-center my-2" data-testid={`dm-system-${m.message_id}`}>
        <span className="font-mono text-[10px] uppercase tracking-wide text-teal-300 border border-teal-300/30 bg-teal-300/5 px-2 py-1">
          <ShieldCheck className="w-3 h-3 inline mr-1" /> {m.text} · {timeAgo(m.created_at)}
        </span>
      </div>
    );
  }
  const support = m.sender_role === "admin_support";
  return (
    <div className={`flex ${support ? "justify-center" : "justify-start"}`} data-testid={`dm-msg-${m.message_id}`}>
      <div className={`max-w-[85%] px-3 py-2 text-sm font-body border ${support ? "border-olive-bright/50 bg-olive/10" : "border-sniper bg-surface-2"}`}>
        <div className="flex items-center gap-1.5 mb-0.5">
          {support ? (
            <span className="font-mono text-[9px] uppercase tracking-wider text-olive-bright border border-olive-bright/50 px-1">OpticSwap Support</span>
          ) : (
            <span className="font-mono text-[10px] text-olive-bright">@{m.sender_username || "member"}</span>
          )}
          {support && m.actor_username && <span className="font-mono text-[9px] text-muted-foreground">by @{m.actor_username}</span>}
        </div>
        <p className="text-foreground whitespace-pre-wrap break-words">{m.text}</p>
        <span className="block text-[10px] font-mono opacity-60 mt-1">{timeAgo(m.created_at)}</span>
      </div>
    </div>
  );
}

export function AdminDmViewer({ caseType, caseId, open, onClose, title }) {
  const [caseData, setCaseData] = useState(null);
  const [activeConv, setActiveConv] = useState(null);
  const [thread, setThread] = useState(null); // {messages, joined, join_session_id, conversation}
  const [tab, setTab] = useState("messages");
  const [accessLog, setAccessLog] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadCase = useCallback(async () => {
    if (!open || !caseType || !caseId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/dm/case/${caseType}/${caseId}`);
      setCaseData(data);
      if ((data.conversations || []).length === 1) setActiveConv(data.conversations[0].conversation_id);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [open, caseType, caseId]);

  useEffect(() => { loadCase(); }, [loadCase]);

  useEffect(() => {
    if (!open) {
      setCaseData(null); setActiveConv(null); setThread(null); setAccessLog([]); setTab("messages"); setText("");
    }
  }, [open]);

  const openConversation = useCallback(async (cid, silent) => {
    if (!cid) return;
    try {
      const { data } = await api.get(`/admin/dm/conversation/${cid}`, { params: { case_type: caseType, case_id: caseId } });
      setThread(data);
    } catch (err) {
      if (!silent) toast.error(formatError(err.response?.data?.detail));
    }
  }, [caseType, caseId]);

  useEffect(() => {
    if (activeConv) { openConversation(activeConv, false); setTab("messages"); }
  }, [activeConv, openConversation]);

  // poll the open conversation while joined (to see member replies)
  useEffect(() => {
    if (!activeConv || tab !== "messages") return;
    const t = setInterval(() => openConversation(activeConv, true), 5000);
    return () => clearInterval(t);
  }, [activeConv, tab, openConversation]);

  const loadAccessLog = async (cid) => {
    try {
      const { data } = await api.get(`/admin/dm/conversation/${cid}/access-log`);
      setAccessLog(data.items || []);
    } catch { /* ignore */ }
  };

  const join = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/dm/conversation/${activeConv}/join`, { case_type: caseType, case_id: caseId });
      toast.success("Joined as OpticSwap Support — both members now see you in the thread.");
      openConversation(activeConv, false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const leave = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/dm/conversation/${activeConv}/leave`, { case_type: caseType, case_id: caseId });
      toast.success("Left the conversation.");
      openConversation(activeConv, false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/dm/conversation/${activeConv}/message`, { case_type: caseType, case_id: caseId, text });
      setText("");
      openConversation(activeConv, false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const conversations = caseData?.conversations || [];
  const eligible = caseData?.eligible;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="bg-surface border-sniper max-w-4xl p-0 gap-0" data-testid="admin-dm-viewer">
        <DialogHeader className="px-5 py-4 border-b border-sniper">
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2 text-foreground">
            <MessageSquare className="w-5 h-5 text-olive-bright" /> Member DM — dispute review
          </DialogTitle>
          <DialogDescription className="font-mono text-[11px] uppercase text-muted-foreground">
            {title || `${caseType} ${caseId}`} · every open, join & message is audited · 7-year retention
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-muted-foreground font-mono text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading case…</div>
        ) : !eligible ? (
          <div className="p-8 text-center" data-testid="dm-not-eligible">
            <Lock className="w-8 h-8 text-blaze mx-auto mb-2" />
            <p className="font-heading uppercase text-foreground">Not accessible</p>
            <p className="text-sm font-body text-muted-foreground mt-1">
              This order is not disputed and has no open chargeback. Member DMs are only reachable from a disputed order or a moderation report.
            </p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-8 text-center" data-testid="dm-no-conversations">
            <MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="font-heading uppercase text-foreground">No conversation found</p>
            <p className="text-sm font-body text-muted-foreground mt-1">These two members never messaged each other on OpticSwap for this case.</p>
          </div>
        ) : (
          <div className="grid grid-cols-[220px_1fr] min-h-[460px] max-h-[70vh]">
            {/* conversation list */}
            <div className="border-r border-sniper overflow-y-auto" data-testid="dm-conv-list">
              {conversations.map((c) => (
                <button key={c.conversation_id} onClick={() => setActiveConv(c.conversation_id)}
                  data-testid={`dm-conv-${c.conversation_id}`}
                  className={`w-full text-left p-3 border-b border-sniper transition-colors ${activeConv === c.conversation_id ? "bg-surface-2" : "hover:bg-surface-2"}`}>
                  <p className="text-xs font-body text-foreground truncate">{c.listing_title || "Listing"}</p>
                  <p className="text-[10px] font-mono text-muted-foreground truncate">
                    {(c.participants || []).map((p) => `@${p.username}`).join(" · ")}
                  </p>
                  <p className="text-[10px] font-mono text-muted-foreground/70 mt-0.5">{c.message_count} msgs{c.support_active ? " · support active" : ""}</p>
                </button>
              ))}
            </div>

            {/* thread */}
            <div className="flex flex-col min-w-0">
              {!activeConv ? (
                <div className="flex-1 flex items-center justify-center text-muted-foreground font-mono text-sm uppercase">Select a conversation</div>
              ) : (
                <>
                  <div className="flex border-b border-sniper">
                    <button onClick={() => setTab("messages")} data-testid="dm-tab-messages"
                      className={`px-4 py-2.5 font-mono uppercase text-xs transition-colors ${tab === "messages" ? "text-foreground border-b-2 border-olive-bright" : "text-muted-foreground hover:text-foreground"}`}>
                      <Eye className="w-3.5 h-3.5 inline mr-1" /> Messages
                    </button>
                    <button onClick={() => { setTab("access"); loadAccessLog(activeConv); }} data-testid="dm-tab-access"
                      className={`px-4 py-2.5 font-mono uppercase text-xs transition-colors ${tab === "access" ? "text-foreground border-b-2 border-olive-bright" : "text-muted-foreground hover:text-foreground"}`}>
                      <History className="w-3.5 h-3.5 inline mr-1" /> Admin access
                    </button>
                    <div className="ml-auto flex items-center px-3">
                      {thread?.joined ? (
                        <button onClick={leave} disabled={busy} data-testid="dm-leave-btn"
                          className="inline-flex items-center gap-1 border border-blaze/50 text-blaze hover:bg-blaze/10 font-mono uppercase text-[10px] px-2 py-1 transition-colors disabled:opacity-50">
                          <LogOut className="w-3 h-3" /> Leave
                        </button>
                      ) : (
                        <button onClick={join} disabled={busy} data-testid="dm-join-btn"
                          className="inline-flex items-center gap-1 bg-olive hover:bg-olive-bright text-white font-mono uppercase text-[10px] px-2 py-1 transition-colors disabled:opacity-50">
                          <LogIn className="w-3 h-3" /> Join as Support
                        </button>
                      )}
                    </div>
                  </div>

                  {tab === "messages" ? (
                    <>
                      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="dm-message-thread">
                        {(thread?.messages || []).length === 0 && <p className="text-center text-xs font-mono text-muted-foreground py-6">No messages.</p>}
                        {(thread?.messages || []).map((m) => <MessageRow key={m.message_id} m={m} />)}
                      </div>
                      {thread?.joined ? (
                        <form onSubmit={sendMsg} className="p-2.5 border-t border-sniper flex gap-2">
                          <input value={text} onChange={(e) => setText(e.target.value)} data-testid="dm-support-input"
                            placeholder="Reply as OpticSwap Support…" className="flex-1 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-sm text-foreground transition-colors" />
                          <button type="submit" disabled={busy} data-testid="dm-support-send" className="bg-olive hover:bg-olive-bright text-white px-3 transition-colors disabled:opacity-50"><Send className="w-4 h-4" /></button>
                        </form>
                      ) : (
                        <div className="p-2.5 border-t border-sniper flex items-center gap-2 text-muted-foreground" data-testid="dm-readonly-note">
                          <Eye className="w-3.5 h-3.5 shrink-0" />
                          <span className="font-mono text-[10px] uppercase">Read-only. Join as Support to reply — members will be told you joined.</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex-1 overflow-y-auto p-3" data-testid="dm-access-log">
                      {accessLog.length === 0 && <p className="text-center text-xs font-mono text-muted-foreground py-6">No admin access recorded yet.</p>}
                      <ul className="space-y-2">
                        {accessLog.map((a) => (
                          <li key={a.event_id} className="border-l-2 border-olive-bright/60 pl-3 py-1" data-testid={`access-row-${a.event_id}`}>
                            <p className="text-xs font-body text-foreground">{a.summary}</p>
                            <p className="text-[10px] font-mono uppercase text-muted-foreground">
                              @{a.actor_username} · {(a.action || "").replace("dm.", "")} · {timeAgo(a.created_at)}
                              {a.meta?.case_type && <> · {a.meta.case_type} {a.meta.case_id}</>}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AdminDmViewer;
